import React, { useContext } from 'react';
import styled from 'styled-components';
import { Title } from '../../containers/DefaultStyles/Shared';
import { isPortrait } from '../../utils/size';
import { getImageUrl } from '../../utils/imageLoading';
import ThemeContext from '../../utils/theming';

const horizontalBlocks = `
    height: 100%;
    margin: 0 0 0 15px;
    &:first-child {
        width: 55%;
        margin: 0 15px 0 0;
        @media (max-width: 36em) {
            width 40%;
        }
    }

    &:last-child {
        width: 45%;
    }
`;
const verticalBlocks = `
    width: 100%;
    &:first-child {
        display: flex;
        flex: .25;
    }
    &:last-child {
        display: flex;
        justify-content: flex-end;
        flex: 2;
    }
`;

export const Block = styled.div`
    ${() => (isPortrait() ? verticalBlocks : horizontalBlocks)}
`;

export const FadeContianer = styled.div`
    &.cross-fade-leave {
        opacity: 1;
    }
    &.cross-fade-leave.cross-fade-leave-active {
        opacity: 0;
        transition: opacity 500ms ease-out;
    }

    &.cross-fade-enter {
        opacity: 0;
    }
    &.cross-fade-enter.cross-fade-enter-active {
        opacity: 1;
        transition: opacity 500ms ease-in;
    }

    &.cross-fade-height {
        transition: height 0.5s ease-in-out;
    }
`;

export const SliderTitleContainer = styled(Title)`
    font-family: Averta;
    margin: 0;
    font-weight: 300;
    line-height: 1.21;

    span {
        font-weight: 800;
        color: ${({ theme }) => theme.colors.primary};
        cursor: pointer;
    }
`;

export const SliderDescriptionContainer = styled.p`
    font-family: Lato;
    margin: 12px 0 0 0;
    color: black;

    span {
        font-weight: 800;
        color: ${({ theme }) => theme.colors.primary};
        cursor: pointer;
    }
`;

export const SliderDescription = props => (
    <SliderDescriptionContainer theme={useContext(ThemeContext)} {...props} />
);
export const SliderTitle = props => (
    <SliderTitleContainer theme={useContext(ThemeContext)} {...props} />
);

export const CurrentAnswer = styled.div`
    pointer-events: none;
    position: relative;
    width: 100%;
    height: 100%;

    span {
        width: 55%;
        position: absolute;
        font-size: 24px;
        font-weight: 300;
        line-height: 1.08;
        text-align: center;
        top: 50%;
        margin: 0;
        padding: 0;
        left: 50%;
        transform: translate(-50%, -50%);
    }
`;

export const SliderWrapper = styled.div`
    display: inline-block;
    position: relative;
    pointer-events: ${({ allowClick }) => (allowClick ? 'auto' : 'none')};
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
`;

export const UserSliderThumb = styled.div`
    cursor: pointer;
    opacity: 1;
    ${({ visible, fadeOutOnAnswer }) =>
        !visible &&
        fadeOutOnAnswer &&
        `   opacity: 0;
            transition: opacity 500ms linear;
        `}
    width: 50%;
    pointer-events: all;
    position: absolute;
    left: 50%;
    top: 50%;
    transform-origin: 0 50%;
    ${({ customColor }) => !!customColor && `path { stroke: ${customColor} !important; }`}
    ${({ rotateOffset }) =>
        `transform: rotate(${rotateOffset}deg) scaleX(-1) !important;`}

    &:after {
        content: '';
        position: absolute;
        display: block;
        top: -15px;
        border-radius: ${({ thumbSize }) => thumbSize / 2}px;
        width: ${({ thumbSize }) => thumbSize}px;
        height: ${({ thumbSize }) => thumbSize}px;
        right: ${({ strokeWidth, thumbSize }) =>
            Math.floor(strokeWidth / 2 - thumbSize / 2)}px;
        ${({ rotateOffset }) => `transform: rotate(${rotateOffset}deg);`}
        ${({ customColor }) => !!customColor && `background-color: ${customColor};`}
        ${({ customLogo }) => !!customLogo && `background-image: url('${customLogo}');`}
        background-position: center;
        background-repeat: no-repeat;
        background-size: cover;
    }

    svg {
        width: 79px !important;
        height: 79px !important;
        position: absolute !important;
        transform: translate(68%,-50%) !important;
    }
`;

export const WrongAnswerThumb = styled(UserSliderThumb)`
    opacity: 0;
    ${({ visible }) =>
        !!visible &&
        `   opacity: 1;
            transition: opacity 300ms linear;
        `}
    &:after {
        background-color: ${({ color }) => color};
        background-size: 12px;
        background-image: ${() =>
            `url(${getImageUrl('radial-slider-x.png', 'DEFAULT_IMAGES_URL')})`};
    }
`;

export const CorrectAnswerThumb = styled(UserSliderThumb)`
    opacity: 0;
    ${({ visible, rotateOffsetFinal, animationDuration }) =>
        !!visible &&
        `   opacity: 1;
            transform: rotate(${rotateOffsetFinal}deg) scaleX(-1) !important;
            transition: opacity 500ms linear, transform ${animationDuration}ms linear;
            transition-delay: 250ms;
            &:after {
                transform: rotate(${rotateOffsetFinal}deg) !important;
                transition: transform ${animationDuration}ms linear;
                transition-delay: 250ms;
            }
        `}
    &:after {
        background-color: ${({ customColor }) => `${customColor}`};
        background-size: 12px;
        background-image: ${() =>
            `url(${getImageUrl('radial-slider-check.png', 'DEFAULT_IMAGES_URL')})`};
    }
`;

export const CircleWrapper = styled.div`
    position: relative;
    display: flex;
    flex: 1;
    height: 100%;
    margin-top: ${({ isPortrait }) => (isPortrait ? 30 : 0)}px;
    transition: margin-top 200ms linear;
    ${({ isAnswered, hasNextAction }) =>
        isAnswered && hasNextAction && !isPortrait() && `margin-top: -25px;`}
`;

export const AnswerGroup = styled.ul`
    position: absolute;
    top: calc(50% - 10px);
    left: calc(50%);
    transform: rotate(-135deg) translateX(-50%);
    margin: 0;
    padding: 0;
    list-style: none;
`;

export function calcX(centerX, radius, angle) {
    const angleInRadians = ((angle - 90) * Math.PI) / 180.0;
    return centerX + radius * Math.cos(angleInRadians);
}

export function calcY(centerY, radius, angle) {
    const angleInRadians = ((angle - 90) * Math.PI) / 180.0;
    return centerY + radius * Math.sin(angleInRadians);
}

export const Answer = styled.li`
    font-size: 12px;
    color: ${({ isBYOBDialPreviewNumbers }) => isBYOBDialPreviewNumbers ? '#c9c3c3' : 'black'};
    ${({ distanceFromCenter, sectionWidth }) => `
        transform:
            translate(
                ${calcX(0, distanceFromCenter, sectionWidth)}px,
                ${calcY(0, distanceFromCenter, sectionWidth)}px
            )
            rotate(135deg);
    `};
    &:after {
        position: absolute;
        transform: translateX(-50%);
        content: ${({ answer }) => `"${answer}"`};
    }
`;
